// frontend/src/pages/professions/ProfessionPage.js
//
// THE routed component for every profession landing page. It takes a `slug`
// and nothing else, so appRoutes.js routes the whole family straight off the
// registry and adding a profession touches no file in pages/ at all. It
// replaces the per-profession wrapper modules (LawyersPage.js and the five
// that were going to follow it), which existed only because a `lazy()` cannot
// be handed its content by the route element without pulling that content into
// the eager bundle.
//
// THE POINT OF THE INDIRECTION, unchanged from those wrappers: a profession's
// copy must never ride in the main chunk. A typo in one profession's strings
// would blank the whole SPA rather than one route, and every visitor would pay
// for words written for a profession that is not theirs. So this module
// statically imports NEITHER the page shell nor any content module -
// `src/__tests__/professionRegistry.test.js` asserts that over the source, the
// way meshSplitBoundary.test.js guards the mesh's split.
//
// ONE NETWORK HOP, NOT TWO. Both imports fire together inside ONE `lazy()`
// factory, so the shell chunk and the content chunk download in parallel and
// the page suspends once. Putting the content behind its own nested
// <Suspense> inside an already-lazy page would waterfall them - a second round
// trip before a paid-acquisition landing page can paint - and would add a
// boundary to a page whose prerendered snapshot is hydrated (React 19 throws a
// root away at a markerless boundary; see Homepage.js's useHydrated note).
// There is no boundary here: the suspension bubbles to App.js's own
// <Suspense fallback={<PageLoadingFallback />}>, which every lazy route
// already uses.
//
// PRELOADABLE (#464): the pages are prerendered and HYDRATED, and a hydrating
// route must not suspend at all (utils/lazyWithPreload.js has why). So each
// slug's component is a lazyWithPreload, and appRoutes.js's `preloadRouteForPath`
// calls `lazyProfessionPage(slug).preload()` before src/index.js hydrates -
// the first render then finds the page already loaded and renders it directly.

import React from "react";
import lazyWithPreload from "../../utils/lazyWithPreload";

// One lazy component per slug, built once. React remounts a subtree whenever
// the element TYPE changes, so a fresh `lazy()` per render would tear the page
// down and re-fetch on every parent render. Built once also means one
// `preload()` promise per slug, shared by the boot preload and the render.
const pages = new Map();

/**
 * The lazy component for one profession slug - a lazyWithPreload, so it also
 * carries `.preload()` (appRoutes.js's route preload uses it).
 *
 * The dynamic specifier is a template, which is what makes webpack emit one
 * chunk per file in `src/content/professions/` and resolve the right one at
 * runtime. `webpackExclude` keeps the two non-content modules in that folder
 * out of it: `registry.js` already rides in the eager bundle (appRoutes.js and
 * HomeNav read it) and `schema.js` is a build-time validator, so emitting lazy
 * chunks for either is dead weight.
 *
 * Exported for the test that pins the caching contract; appRoutes.js goes through
 * the component below.
 *
 * @param {string} slug a `PROFESSION_PAGES` slug, i.e. the content module's
 *   filename. Never user input - appRoutes.js maps over the registry.
 */
export function lazyProfessionPage(slug) {
  if (!pages.has(slug)) {
    pages.set(
      slug,
      lazyWithPreload(async () => {
        const [shell, content] = await Promise.all([
          import("../ProfessionLanding"),
          import(
            /* webpackExclude: /(registry|schema)\.js$/ */
            `../../content/professions/${slug}.js`
          ),
        ]);
        const ProfessionLanding = shell.default;
        return { default: () => <ProfessionLanding content={content.default} /> };
      })
    );
  }
  return pages.get(slug);
}

/**
 * @param {object} props
 * @param {string} props.slug the registry slug for this route.
 */
const ProfessionPage = ({ slug }) => {
  const Page = lazyProfessionPage(slug);
  return <Page />;
};

/**
 * Start loading the page for `slug` (#464) - what appRoutes.js's preloadRouteForPath
 * calls, with the route element's props, before a prerendered profession page
 * is hydrated.
 *
 * @param {{slug: string}} props the route element's props.
 * @returns {Promise<unknown>}
 */
ProfessionPage.preload = ({ slug }) => lazyProfessionPage(slug).preload();

export default ProfessionPage;
